import { Link } from "react-router-dom";
import {
  SAMPLE_HEADLINE,
  SAMPLE_HEADLINE_METRICS,
  SAMPLE_REPORT_LABEL,
  samplePlatformBars,
} from "../../shared/report/sample-headline";
import { cn } from "../lib/utils";

interface SampleReportTeaserProps {
  /** Router path to the full sample, query string included. */
  to: string;
  className?: string;
}

/** Compact, static card for the pre-auth sample report. */
export function SampleReportTeaser({ to, className }: SampleReportTeaserProps) {
  return (
    <Link
      to={to}
      aria-label={`${SAMPLE_REPORT_LABEL}. See the full sample report.`}
      className={cn(
        "group block rounded-[20px] border border-neutral-950/5 bg-white p-5 text-ink no-underline shadow-[0_12px_32px_-16px_rgba(16,24,40,0.18)] transition-shadow hover:shadow-[0_18px_40px_-16px_rgba(16,24,40,0.24)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-purple-500 sm:p-6",
        className,
      )}
    >
      <p className="text-xs font-semibold text-purple-700">{SAMPLE_REPORT_LABEL}</p>
      <dl className="mt-4 grid grid-cols-3 gap-3 border-b border-neutral-100 pb-4">
        {SAMPLE_HEADLINE_METRICS.map((m) => (
          <div key={m.label} className="min-w-0">
            <dt className="truncate text-[11px] font-medium text-neutral-600">{m.label}</dt>
            <dd className="mt-1 font-display text-xl font-[900] leading-none tracking-[-0.02em] tabular-nums">
              {m.value.toLocaleString("en-US")}
            </dd>
          </div>
        ))}
      </dl>
      <ul role="list" className="mt-4 space-y-2 text-[13px]">
        {samplePlatformBars(3).map((p) => (
          <li key={p.platform} className="grid grid-cols-[72px_1fr_48px] items-center gap-3">
            <span className="font-medium text-neutral-700">{p.platform}</span>
            <span aria-hidden="true" className="h-1.5 overflow-hidden rounded-full bg-neutral-100">
              <span
                className="block h-full rounded-full bg-purple-500"
                style={{ width: `${p.pct}%` }}
              />
            </span>
            <span className="text-right font-bold tabular-nums">
              {p.sessions.toLocaleString("en-US")}
            </span>
          </li>
        ))}
      </ul>
      <p className="mt-4 flex flex-wrap items-center justify-between gap-x-3 gap-y-1 text-sm">
        <span className="text-neutral-600">
          UTM Health{" "}
          <span className="font-bold text-ink tabular-nums">
            {SAMPLE_HEADLINE.utmHealthScore}/100
          </span>
        </span>
        <span className="ts-link whitespace-nowrap">
          See the full sample <span className="ts-link-arrow">→</span>
        </span>
      </p>
    </Link>
  );
}
